import React from 'react'
import { Navbar } from './navbar'
import { useThemeStyle } from '@/components/theme/ThemeProvider'
import { cn } from '@/lib/utils'

interface BaseLayoutProps {
  children: React.ReactNode
  className?: string
  variant?: 'default' | 'centered' | 'wide' | 'minimal'
}

export function BaseLayout({ children, className, variant = 'default' }: BaseLayoutProps) {
  const { theme } = useThemeStyle()

  const containerVariants = {
    default: 'container mx-auto px-4',
    centered: 'container mx-auto px-4 max-w-4xl',
    wide: 'container mx-auto px-4 max-w-7xl',
    minimal: 'mx-auto px-6 max-w-3xl'
  }

  return (
    <div 
      className="min-h-screen bg-background font-[--theme-font-family] transition-all duration-[--theme-duration] ease-[--theme-easing]"
      style={{
        fontFamily: theme.styles.typography.fontFamily,
        fontWeight: theme.styles.typography.bodyWeight
      }}
    >
      <Navbar />
      <main 
        className={cn(
          'pt-[--theme-navbar-height]',
          containerVariants[variant],
          className
        )}
        style={{
          paddingTop: theme.components.navbar.height
        }}
      >
        {children}
      </main>
    </div>
  )
}

// Componente específico para páginas de conteúdo
interface ContentLayoutProps extends BaseLayoutProps {
  title?: string
  subtitle?: string
  showHeader?: boolean
}

export function ContentLayout({ 
  children, 
  title, 
  subtitle, 
  showHeader = true,
  className,
  variant = 'centered'
}: ContentLayoutProps) {
  const { theme } = useThemeStyle()

  return (
    <BaseLayout variant={variant} className={className}>
      {showHeader && (title || subtitle) && (
        <header 
          className="py-8 mb-8 text-center"
          style={{ paddingTop: theme.styles.spacing.section }}
        >
          {title && (
            <h1 
              className="text-4xl mb-4 text-foreground"
              style={{ 
                fontWeight: theme.styles.typography.headingWeight,
                marginBottom: theme.styles.spacing.card
              }}
            >
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </header>
      )}
      {children}
    </BaseLayout>
  )
}

// Componente para seções com espaçamento consistente
interface SectionProps {
  children: React.ReactNode
  className?: string
  background?: 'default' | 'muted' | 'accent'
}

export function Section({ children, className, background = 'default' }: SectionProps) {
  const { theme } = useThemeStyle()

  const backgroundVariants = {
    default: 'bg-background',
    muted: 'bg-muted/50',
    accent: 'bg-accent/10'
  }

  return (
    <section 
      className={cn(
        backgroundVariants[background],
        'py-[--theme-spacing-section]',
        className
      )}
      style={{
        paddingTop: theme.styles.spacing.section,
        paddingBottom: theme.styles.spacing.section
      }}
    >
      {children}
    </section>
  )
}